import { useEffect, useState } from 'react'
import { Copy, Link2, Loader2, Mail, RefreshCw, Trash2, UserRound, Users, X } from 'lucide-react'
import { useAppStore } from '@core/store'
import { partilhasApi } from './partilhasApi'
import {
  avisosDePrivacidade,
  emailValido,
  juntarLigacoes,
  NOME_DO_TIPO,
  resumoDasPecas,
  textoDaLigacao,
  textoDeRetirar,
  visivelPara,
  type LigacaoDeEntrada,
  type PartilhaInfo,
  type PessoaEncontrada,
} from './partilhasRegras'
import {
  fecharJanelaPartilhar,
  useJanelaPartilhar,
  type OpcoesDaCriacao,
  type PedidoDeJanela,
} from './partilhasEstado'

// ---------------------------------------------------------------------------
// A janela "Partilhar com...", a mesma para modelos, BCF, triagens e a
// sessão inteira (que pede um nome e, se for preciso, o código da
// triagem, e mostra o progresso enquanto os modelos sobem).
//
// Dois modos:
//  - por partilhar (`partilhaId` null): escolhe-se o workspace inteiro
//    e/ou pessoas, e "Partilhar" cria o recurso partilhado e dá-lhes
//    acesso;
//  - partilhado: a lista de acessos (só para quem partilhou), com
//    acrescentar, remover cada um, o workspace inteiro e "Retirar a
//    partilha". Quem só tem acesso vê o resumo "Visível para".
//
// A pesquisa de pessoas só encontra colegas e quem já recebeu uma
// partilha; um email escrito por inteiro aparece tal como foi escrito,
// sem dizer se tem conta (ver backend/core/partilhas_regras.py).
// ---------------------------------------------------------------------------

const BOTAO =
  'rounded border border-[var(--color-border)] px-2 py-0.5 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-40'

function mensagem(err: unknown): string {
  return err instanceof Error ? err.message : 'Não foi possível falar com o servidor.'
}

/** O anfitrião da janela: montado uma vez no layout do workspace. */
export default function JanelaPartilharHost() {
  const pedido = useJanelaPartilhar()
  if (!pedido) return null
  return (
    <Janela
      key={`${pedido.tipo}:${pedido.partilhaId ?? pedido.nome}`}
      pedido={pedido}
    />
  )
}

function Janela({ pedido }: { pedido: PedidoDeJanela }) {
  const pushToast = useAppStore((s) => s.pushToast)
  const [partilhaId, setPartilhaId] = useState(pedido.partilhaId)
  const [info, setInfo] = useState<PartilhaInfo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [avisos, setAvisos] = useState<string[]>([])
  // As ligações de entrada das pessoas a quem se acabou de dar acesso,
  // para copiar e mandar já (o email pode demorar; pedido do Rui,
  // 15/10/2026). Vivem só nesta janela.
  const [ligacoes, setLigacoes] = useState<LigacaoDeEntrada[]>([])
  const [copiada, setCopiada] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState<PessoaEncontrada[]>([])
  // Por partilhar: o que se vai dar ao partilhar. "Todo o workspace"
  // começa desligado: sem escolha, fica só para quem partilha (decisão
  // do Rui, 15/10/2026).
  const [workspaceInteiro, setWorkspaceInteiro] = useState(false)
  const [pendentes, setPendentes] = useState<string[]>([])
  // A sessão: o nome, o código da triagem (só se for preciso), e o que a
  // preparação diz enquanto corre (modelos a subir) e no fim.
  const [nome, setNome] = useState(pedido.campos?.nome ?? '')
  const [codigo, setCodigo] = useState(pedido.campos?.codigoTriagem ?? '')
  const [progresso, setProgresso] = useState<string | null>(null)
  const [avisosDaPreparacao, setAvisosDaPreparacao] = useState<string[]>([])

  const carregar = async (id: string) => {
    try {
      setInfo(await partilhasApi.ver(id))
      setErro(null)
    } catch (err) {
      setErro(mensagem(err))
    }
  }

  useEffect(() => {
    if (!partilhaId) return
    let vivo = true
    void partilhasApi
      .ver(partilhaId)
      .then((i) => {
        if (vivo) setInfo(i)
      })
      .catch((err) => {
        if (vivo) setErro(mensagem(err))
      })
    return () => {
      vivo = false
    }
  }, [partilhaId])

  // A pesquisa espera que se pare de escrever.
  useEffect(() => {
    const termo = q.trim()
    const t = window.setTimeout(() => {
      if (termo.length < 2) {
        setResultados([])
        return
      }
      void partilhasApi
        .pesquisar(termo)
        .then(setResultados)
        .catch(() => setResultados([]))
    }, 250)
    return () => window.clearTimeout(t)
  }, [q])

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fecharJanelaPartilhar()
    }
    window.addEventListener('keydown', aoTeclar)
    return () => window.removeEventListener('keydown', aoTeclar)
  }, [])

  const depois = (i: PartilhaInfo) => {
    setInfo(i)
    setAvisos(i.avisos)
    setLigacoes((l) => juntarLigacoes(l, i.ligacoes))
    setErro(null)
    pedido.aoMudar?.()
  }

  const copiar = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      setCopiada(url)
    } catch {
      setErro('O browser não deixou copiar: seleccione a ligação e copie à mão.')
    }
  }

  /** Uma ligação nova para um convite pendente, sem mandar outro email. */
  const novaLigacao = (email: string) => {
    if (!partilhaId) return
    void correr(async () => {
      const i = await partilhasApi.acrescentar(partilhaId, [email], false, false)
      depois(i)
      const nova = i.ligacoes?.find((l) => l.email === email)
      if (nova) await copiar(nova.url)
      else setAvisos((l) => [...l, `${email} já entrou pelo convite: não há ligação nova a gerar.`])
    })
  }

  const correr = async (fn: () => Promise<void>) => {
    setOcupado(true)
    try {
      await fn()
    } catch (err) {
      setErro(mensagem(err))
    } finally {
      setOcupado(false)
    }
  }

  const escolher = (email: string) => {
    setQ('')
    setResultados([])
    if (!partilhaId) {
      setPendentes((l) => (l.includes(email) ? l : [...l, email]))
      return
    }
    void correr(async () => depois(await partilhasApi.acrescentar(partilhaId, [email])))
  }

  const opcoes = (): OpcoesDaCriacao => ({
    nome: pedido.campos?.nome !== undefined ? nome : undefined,
    codigoTriagem: pedido.campos?.codigoTriagem !== undefined ? codigo : undefined,
    progresso: setProgresso,
    aviso: (texto) => setAvisosDaPreparacao((l) => (l.includes(texto) ? l : [...l, texto])),
  })

  const partilhar = () =>
    correr(async () => {
      if (!pedido.criar) return
      if (pedido.campos?.nome !== undefined && !nome.trim()) {
        throw new Error('Dê um nome à sessão (por exemplo, o código da obra).')
      }
      setAvisosDaPreparacao([])
      const id = await pedido.criar(workspaceInteiro, opcoes())
      setPartilhaId(id)
      if (pendentes.length > 0) depois(await partilhasApi.acrescentar(id, pendentes))
      else await carregar(id)
      setPendentes([])
      pedido.aoMudar?.()
    })

  const mudarWorkspace = (sim: boolean) => {
    if (!partilhaId || !info) return
    void correr(async () => {
      if (sim) {
        depois(await partilhasApi.acrescentar(partilhaId, [], true))
        return
      }
      const a = info.acessos.find((x) => x.tipo === 'workspace')
      if (a) depois(await partilhasApi.remover(partilhaId, a.id))
    })
  }

  const remover = (acessoId: string) => {
    if (!partilhaId) return
    void correr(async () => depois(await partilhasApi.remover(partilhaId, acessoId)))
  }

  const retirar = () => {
    if (!partilhaId) return
    const texto = textoDeRetirar(pedido.tipo, info?.nome ?? pedido.nome, {
      pecas: info?.pecas,
      sessoes: info?.sessoes,
    })
    if (!window.confirm(texto)) return
    void correr(async () => {
      depois(await partilhasApi.retirar(partilhaId))
      pushToast(
        pedido.tipo === 'sessao'
          ? 'Partilha da sessão retirada: saiu tudo o que tinha sido partilhado por causa dela.'
          : 'Partilha retirada. Só o dono continua com acesso.',
        { type: 'success' },
      )
    })
  }

  const atualizar = () => {
    if (!partilhaId || !pedido.atualizar) return
    const fazer = pedido.atualizar
    void correr(async () => {
      setAvisosDaPreparacao([])
      await fazer(opcoes())
      await carregar(partilhaId)
      pedido.aoMudar?.()
      pushToast('Sessão partilhada atualizada com o que está aberto agora.', { type: 'success' })
    })
  }

  const emailEscrito = emailValido(q)
  const gerir = !partilhaId || !!info?.pode_gerir
  // Sem o nome (ainda a carregar), fica só "todo o workspace": antes
  // lia-se "Todo o workspace deste workspace".
  const nomeDoWorkspace = info?.workspace_nome ?? pedido.workspaceNome ?? ''

  // Banda dos diálogos (CAMADA.dialogos), ver core/layout/camadas.ts.
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/50 p-4 pt-16"
      role="dialog"
      aria-modal="true"
      aria-label="Partilhar com"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) fecharJanelaPartilhar()
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-md flex-col gap-2 overflow-y-auto rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-4 text-xs text-[var(--color-text)] shadow-xl">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-medium">
              {pedido.tipo === 'sessao' ? 'Partilhar a sessão com…' : 'Partilhar com…'}
            </h2>
            <p className="truncate text-[var(--color-text-dim)]" title={pedido.nome}>
              {NOME_DO_TIPO[pedido.tipo]}: {info?.nome ?? pedido.nome}
            </p>
          </div>
          <button
            type="button"
            onClick={fecharJanelaPartilhar}
            aria-label="Fechar"
            className="rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
          >
            <X size={14} />
          </button>
        </div>

        {!partilhaId && pedido.descricao && (
          <p className="text-[var(--color-text-muted)]">{pedido.descricao}</p>
        )}
        {partilhaId && info?.pecas && info.pecas.length > 0 && (
          <p className="text-[var(--color-text-muted)]" data-testid="sessao-inclui">
            Inclui {resumoDasPecas(info.pecas)}.
          </p>
        )}
        {!partilhaId && pedido.campos?.nome !== undefined && (
          <label className="flex flex-col gap-0.5">
            <span className="text-[var(--color-text-muted)]">Nome da sessão</span>
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={120}
              disabled={ocupado}
              className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 outline-none focus:border-[var(--color-accent)]"
            />
          </label>
        )}
        {!partilhaId && pedido.campos?.codigoTriagem !== undefined && (
          <label className="flex flex-col gap-0.5">
            <span className="text-[var(--color-text-muted)]">
              Código da triagem dos conflitos (ainda não está partilhada)
            </span>
            <input
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Por exemplo, Cha139"
              maxLength={60}
              disabled={ocupado}
              className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 outline-none focus:border-[var(--color-accent)]"
            />
          </label>
        )}

        <p data-testid="partilha-visivel-para" className="rounded bg-[var(--color-accent-bg)] px-2 py-1">
          {partilhaId
            ? info
              ? visivelPara(info)
              : 'A ler…'
            : workspaceInteiro || pendentes.length > 0
              ? `Visível para: ${[
                  ...(workspaceInteiro ? [`todo o workspace${nomeDoWorkspace ? ` ${nomeDoWorkspace}` : ''}`] : []),
                  ...pendentes,
                ].join(', ')}.`
              : 'Visível para: só si.'}
        </p>

        {gerir ? (
          <>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={partilhaId ? !!info?.workspace_inteiro : workspaceInteiro}
                disabled={ocupado || (!!partilhaId && !info)}
                onChange={(e) =>
                  partilhaId ? mudarWorkspace(e.target.checked) : setWorkspaceInteiro(e.target.checked)
                }
              />
              <Users size={12} aria-hidden />
              Todo o workspace{nomeDoWorkspace ? ` ${nomeDoWorkspace}` : ''} (membros com o módulo)
            </label>

            <div className="flex flex-col gap-1">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nome ou email de uma pessoa"
                aria-label="Procurar uma pessoa por nome ou email"
                className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 outline-none focus:border-[var(--color-accent)]"
              />
              <p className="text-[10px] text-[var(--color-text-dim)]">
                Só aparecem colegas deste workspace e pessoas com quem já partilhou. Para outra
                pessoa, escreva o email completo: se não tiver conta, recebe um convite de
                convidado, que só lhe dá acesso ao que lhe partilharem. A ligação do convite
                aparece aqui para a copiar e enviar logo, sem esperar pelo email.
              </p>
              {(resultados.length > 0 || emailEscrito) && (
                <ul className="flex flex-col rounded border border-[var(--color-border)]">
                  {resultados.map((p) => (
                    <li key={p.email}>
                      <button
                        type="button"
                        disabled={ocupado}
                        onClick={() => escolher(p.email)}
                        className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-[var(--color-accent-bg)]"
                      >
                        {p.conhecido ? <UserRound size={12} aria-hidden /> : <Mail size={12} aria-hidden />}
                        <span className="min-w-0 flex-1 truncate">
                          {p.nome ? `${p.nome} (${p.email})` : p.email}
                        </span>
                      </button>
                    </li>
                  ))}
                  {emailEscrito && !resultados.some((p) => p.email === emailEscrito) && (
                    <li>
                      <button
                        type="button"
                        disabled={ocupado}
                        onClick={() => escolher(emailEscrito)}
                        className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-[var(--color-accent-bg)]"
                      >
                        <Mail size={12} aria-hidden />
                        <span className="min-w-0 flex-1 truncate">{emailEscrito}</span>
                      </button>
                    </li>
                  )}
                </ul>
              )}
            </div>

            {!partilhaId && pendentes.length > 0 && (
              <ul className="flex flex-col gap-0.5">
                {pendentes.map((e) => (
                  <li key={e} className="flex items-center gap-2">
                    <Mail size={11} aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{e}</span>
                    <button
                      type="button"
                      className={BOTAO}
                      onClick={() => setPendentes((l) => l.filter((x) => x !== e))}
                    >
                      Remover
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {partilhaId && info && info.acessos.length > 0 && (
              <ul className="flex flex-col gap-0.5" aria-label="Acessos">
                {info.acessos.map((a) => (
                  <li key={a.id} className="flex items-center gap-2">
                    {a.tipo === 'workspace' ? (
                      <Users size={11} aria-hidden />
                    ) : a.tipo === 'convite' ? (
                      <Mail size={11} aria-hidden />
                    ) : (
                      <UserRound size={11} aria-hidden />
                    )}
                    <span className="min-w-0 flex-1 truncate" title={a.email ?? undefined}>
                      {a.rotulo}
                      {a.tipo === 'pessoa' && a.email && a.email !== a.rotulo ? ` (${a.email})` : ''}
                      {a.tipo === 'convite' ? ` (${a.estado})` : ''}
                      {a.via_sessao ? (
                        <span className="text-[var(--color-text-dim)]"> · pela sessão "{a.via_sessao}"</span>
                      ) : null}
                    </span>
                    {a.tipo === 'convite' && a.email && !a.via_sessao && (
                      <button
                        type="button"
                        className={`${BOTAO} flex items-center gap-1`}
                        disabled={ocupado}
                        onClick={() => novaLigacao(a.email ?? '')}
                        title="Gera uma ligação nova para este convite e copia-a, sem mandar outro email. A ligação anterior deixa de servir."
                      >
                        <Link2 size={11} aria-hidden />
                        Ligação
                      </button>
                    )}
                    <button
                      type="button"
                      className={BOTAO}
                      disabled={ocupado || !!a.via_sessao}
                      onClick={() => remover(a.id)}
                      title={
                        a.via_sessao
                          ? `Este acesso vem da sessão "${a.via_sessao}": tira-se na partilha da sessão (ou tirando esta peça da sessão).`
                          : pedido.tipo === 'sessao'
                            ? 'Tira o acesso já, à sessão e a tudo o que ela inclui (os acessos que as peças deram por si ficam).'
                            : 'Tira o acesso já: os pedidos seguintes são recusados e as ligações ao vivo fecham.'
                      }
                    >
                      Remover
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <p className="text-[var(--color-text-dim)]">
            Só quem partilhou{info?.dono_nome ? ` (${info.dono_nome})` : ''} muda com quem está
            partilhado.
          </p>
        )}

        {ligacoes.length > 0 && (
          <div
            data-campo="ligacoes-de-entrada"
            className="flex flex-col gap-1.5 rounded border border-[var(--color-accent)] p-2"
          >
            <p className="font-medium">Ligações para enviar já</p>
            {ligacoes.map((l) => (
              <div key={l.email} className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate">{l.email}</span>
                  <button
                    type="button"
                    className={`${BOTAO} flex items-center gap-1`}
                    onClick={() => void copiar(l.url)}
                  >
                    <Copy size={11} aria-hidden />
                    {copiada === l.url ? 'Copiada' : 'Copiar'}
                  </button>
                </div>
                <input
                  readOnly
                  value={l.url}
                  aria-label={`Ligação de entrada para ${l.email}`}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--color-text-muted)]"
                />
                <p className="text-[10px] text-[var(--color-text-dim)]">{textoDaLigacao(l)}</p>
              </div>
            ))}
          </div>
        )}

        {progresso && (
          <p className="flex items-center gap-1.5 text-[var(--color-accent)]" role="status">
            <Loader2 size={12} className="shrink-0 animate-spin" aria-hidden />
            <span className="min-w-0 flex-1">{progresso}</span>
          </p>
        )}
        {[...avisosDaPreparacao, ...avisos.filter((a) => !avisosDaPreparacao.includes(a))].map((a) => (
          <p key={a} className="text-[var(--color-warn-text)]">
            {a}
          </p>
        ))}
        {erro && <p className="text-[var(--color-error-text)]">{erro}</p>}

        <ul className="flex list-disc flex-col gap-0.5 pl-4 text-[10px] text-[var(--color-text-dim)]">
          {avisosDePrivacidade(pedido.tipo).map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {!partilhaId ? (
            <button
              type="button"
              disabled={ocupado || !pedido.criar}
              onClick={() => void partilhar()}
              className="flex items-center gap-1 rounded bg-[var(--color-accent)] px-3 py-1 font-medium text-[var(--color-bg)] disabled:opacity-50"
            >
              {ocupado && <Loader2 size={12} className="animate-spin" />}
              {ocupado ? 'A partilhar…' : 'Partilhar'}
            </button>
          ) : (
            info?.pode_gerir && (
              <>
                {pedido.atualizar && (
                  <button
                    type="button"
                    disabled={ocupado}
                    onClick={atualizar}
                    className={`${BOTAO} flex items-center gap-1`}
                    title="Envia o que mudou desde que a sessão foi partilhada: modelos novos, a triagem, os BCF e os pontos de vista. Quem já tem acesso passa a ver também o que entrou."
                  >
                    <RefreshCw size={11} aria-hidden />
                    Atualizar a sessão
                  </button>
                )}
              <button
                type="button"
                disabled={ocupado || (info.acessos.length === 0)}
                onClick={retirar}
                className="flex items-center gap-1 rounded border border-[var(--color-error-text)] px-2 py-0.5 text-[11px] text-[var(--color-error-text)] disabled:opacity-40"
                title="Tira todos os acessos de uma vez. Não apaga os dados."
              >
                <Trash2 size={11} aria-hidden />
                Retirar a partilha
              </button>
              </>
            )
          )}
          {ocupado && partilhaId && <Loader2 size={12} className="animate-spin" />}
          <button type="button" className={`${BOTAO} ml-auto`} onClick={fecharJanelaPartilhar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
